import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, Download, Printer } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { authApi } from "@/client/features/auth/auth-api";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import type { SessionUser } from "@/shared/types";

const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the six-digit code"),
});

type CodeValues = z.infer<typeof codeSchema>;

interface MfaChallengeProps {
  enrolling: boolean;
  onComplete: (user: SessionUser) => void;
}

export function MfaChallenge({ enrolling: initiallyEnrolling, onComplete }: MfaChallengeProps) {
  const completeSession = useAuthStore((state) => state.completeSession);
  const [enrolling, setEnrolling] = useState(initiallyEnrolling);
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [enrollError, setEnrollError] = useState(false);
  const enrollmentStarted = useRef(false);

  const codeForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });
  const recoveryForm = useForm<{ code: string }>({ defaultValues: { code: "" } });

  const startEnrollment = useCallback(() => {
    enrollmentStarted.current = true;
    setEnrollError(false);
    void authApi
      .startMfaEnrollment()
      .then(setEnrollment)
      .catch((error) => {
        // Allow an in-place retry instead of stranding the user on the
        // "Preparing…" state after a transient failure.
        enrollmentStarted.current = false;
        setEnrollError(true);
        toast.error(
          error instanceof ApiError
            ? error.message
            : "Unable to start authenticator setup. Please try again.",
        );
      });
  }, []);

  useEffect(() => {
    if (!enrolling || enrollment || recoveryCodes || enrollmentStarted.current) {
      return;
    }
    startEnrollment();
  }, [enrolling, enrollment, recoveryCodes, startEnrollment]);

  const submitCode = async ({ code }: CodeValues) => {
    try {
      if (enrolling) {
        const response = await authApi.confirmMfaEnrollment(code);
        setRecoveryCodes(response.recoveryCodes);
        setEnrollment(null);
        return;
      }
      const session = await authApi.verifyMfa(code);
      onComplete(completeSession(session));
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Unable to verify the code.");
    }
  };

  const submitRecovery = async ({ code }: { code: string }) => {
    if (!code.trim()) {
      recoveryForm.setError("code", { message: "Enter a recovery code" });
      return;
    }
    try {
      await authApi.recoverMfa(code);
      setRecoveryMode(false);
      setEnrolling(true);
      enrollmentStarted.current = false;
      recoveryForm.reset();
      toast.message("Recovery code accepted. Set up a replacement authenticator.");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Unable to use the recovery code.");
    }
  };

  const completeEnrollment = async () => {
    try {
      const session = await authApi.completeMfaEnrollment();
      onComplete(completeSession(session));
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Unable to complete MFA setup.");
    }
  };

  if (recoveryCodes) {
    const text = recoveryCodes.join("\n");
    return (
      <Card>
        <CardHeader>
          <CardTitle>Save your recovery codes</CardTitle>
          <CardDescription>
            Each code works once if you lose your authenticator. Store them somewhere secure.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="bg-muted grid gap-2 rounded-md p-4 font-mono text-sm">
            {recoveryCodes.map((code) => (
              <span key={code}>{code}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(text);
                toast.success("Recovery codes copied.");
              }}
            >
              <Copy /> Copy
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
                const link = document.createElement("a");
                link.href = url;
                link.download = "mantle-portal-recovery-codes.txt";
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download /> Download
            </Button>
            <Button type="button" variant="outline" onClick={() => window.print()}>
              <Printer /> Print
            </Button>
          </div>
          <label className="flex items-start gap-2 text-sm print:hidden">
            <input
              type="checkbox"
              className="mt-1"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            I saved these recovery codes. They will not be shown again.
          </label>
          <Button
            type="button"
            disabled={!acknowledged}
            onClick={() => void completeEnrollment()}
            className="print:hidden"
          >
            Continue to the portal
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (recoveryMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Use a recovery code</CardTitle>
          <CardDescription>
            Enter one of the single-use codes you saved during MFA setup.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={recoveryForm.handleSubmit(submitRecovery)} className="grid gap-4">
            <Input
              aria-label="Recovery code"
              autoComplete="one-time-code"
              {...recoveryForm.register("code")}
            />
            {recoveryForm.formState.errors.code ? (
              <p className="text-destructive text-sm">
                {recoveryForm.formState.errors.code.message}
              </p>
            ) : null}
            <Button type="submit" disabled={recoveryForm.formState.isSubmitting}>
              Continue
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRecoveryMode(false)}>
              Back to authenticator code
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{enrolling ? "Set up an authenticator" : "Enter authenticator code"}</CardTitle>
        <CardDescription>
          {enrolling
            ? "Scan this QR code with Microsoft Authenticator, Google Authenticator, 1Password, or another TOTP app."
            : "Enter the six-digit code from your authenticator app."}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {enrolling ? (
          enrollment ? (
            <div className="grid justify-items-center gap-3">
              <div className="bg-card rounded-md p-3">
                <QRCode
                  value={enrollment.otpauthUri}
                  size={184}
                  bgColor="var(--card)"
                  fgColor="var(--card-foreground)"
                />
              </div>
              <div className="grid w-full gap-1">
                <span className="text-muted-foreground text-xs">Manual setup key</span>
                <code className="bg-muted break-all rounded-md p-2 text-sm">
                  {enrollment.secret}
                </code>
              </div>
            </div>
          ) : enrollError ? (
            <div className="grid justify-items-center gap-2">
              <p className="text-muted-foreground text-sm">Couldn't start authenticator setup.</p>
              <Button type="button" variant="outline" onClick={startEnrollment}>
                Try again
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Preparing authenticator setup…</p>
          )
        ) : null}
        <Form {...codeForm}>
          <form onSubmit={codeForm.handleSubmit(submitCode)} className="grid gap-4">
            <FormField
              control={codeForm.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Six-digit code</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={codeForm.formState.isSubmitting || (enrolling && !enrollment)}
            >
              {codeForm.formState.isSubmitting
                ? "Verifying…"
                : enrolling
                  ? "Confirm authenticator"
                  : "Verify"}
            </Button>
          </form>
        </Form>
        {!enrolling ? (
          <Button type="button" variant="link" onClick={() => setRecoveryMode(true)}>
            I lost access to my authenticator
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
